import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

/**
 * FRONT-433 — which chats have actually been *detached* from their reading
 * modal (the user closed the zen modal while the chat was still busy) and
 * therefore deserve a monitored-chat row in the Inspector.
 *
 * Every inbox-modal turn opens a `background_chats` row server-side so it can
 * finish even if the modal closes. But while the user is sitting in the modal
 * watching the answer stream, that turn is normal in-turn processing — NOT a
 * background job — and must not show up in the Inspector with a scary
 * "Working in background…" timer (which reads identically to a real research
 * run). The honest trigger for "this is genuinely backgrounded" is: the modal
 * was closed while the chat was still busy. The modal records that here
 * (`promote(sessionId)`) on unmount; the Inspector only surfaces monitored-chat
 * rows for promoted sessions. Quick turns watched to completion are never
 * promoted, so they stay hidden entirely.
 *
 * FRONT-754: re-keyed from `runId` to `sessionId`. A deep-research turn's own
 * `background_chats` row settles within seconds of approval (the skill
 * dispatch is fire-and-forget), well before the research it kicked off
 * finishes — so a specific run id is the wrong identity to track "is this
 * chat still busy": the chat-status SPINE (which folds in `research_runs` too)
 * is per-SESSION, not per-run, and that spine is now what decides whether a
 * promoted row is still live. Keying by session also means a chat with several
 * turns only ever needs one promoted entry, not one per turn.
 *
 * The persisted shape changed with the re-key, so `STORAGE_VERSION` guards
 * against replaying old, now-meaningless run ids: any stored value from
 * before the bump is dropped rather than "migrated" — there is no client-side
 * runId→sessionId mapping to migrate it through, and a stale run id can never
 * match a real session id, so keeping it would just be dead weight.
 *
 * Persisted to localStorage (and cross-tab synced) so a detached chat's card
 * survives a refresh while it keeps running — mirrors the dismissed/pinned
 * skill-run stores.
 */

const STORAGE_KEY = 'eddy.detached-bg-chats';
const STORAGE_VERSION_KEY = 'eddy.detached-bg-chats.version';
const STORAGE_VERSION = '2'; // FRONT-754: runId-keyed (v1, unversioned) → sessionId-keyed
const MAX_IDS = 200;

function loadIds(): string[] {
  try {
    if (localStorage.getItem(STORAGE_VERSION_KEY) !== STORAGE_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
      return [];
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function persistIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* quota or storage disabled — promotion becomes session-only */
  }
}

export interface DetachedBgChatsStore {
  /** Session ids (FRONT-754 — was run ids before the v2 re-key). */
  ids: Set<string>;
  /** Mark a chat session as detached so the Inspector surfaces it. Idempotent. */
  promote: (sessionId: string) => void;
  /** Drop a session (e.g. on dismiss). No-op if absent. */
  forget: (sessionId: string) => void;
  clear: () => void;
}

export function createDetachedBgChatsStore(): StoreApi<DetachedBgChatsStore> {
  return createStore<DetachedBgChatsStore>(
    'detached-bg-chats-store',
    (set, get) => ({
      ids: new Set(loadIds()),
      promote: (sessionId) => {
        if (!sessionId || get().ids.has(sessionId)) return;
        const arr = [...get().ids, sessionId];
        // FIFO trim so the localStorage entry can't grow without bound after
        // months of use — old sessions age out of the server's recent list.
        const trimmed = arr.length > MAX_IDS ? arr.slice(arr.length - MAX_IDS) : arr;
        persistIds(trimmed);
        set({ ids: new Set(trimmed) });
      },
      forget: (sessionId) => {
        if (!get().ids.has(sessionId)) return;
        const next = [...get().ids].filter((id) => id !== sessionId);
        persistIds(next);
        set({ ids: new Set(next) });
      },
      clear: () => {
        persistIds([]);
        set({ ids: new Set() });
      },
    }),
  );
}

let store: StoreApi<DetachedBgChatsStore> | undefined;

function getStore(): StoreApi<DetachedBgChatsStore> {
  if (!store) {
    store = createDetachedBgChatsStore();
    // Cross-tab sync: a promote/forget in another tab updates this tab's set so
    // the card appears/disappears within ~2 s (the next background-tasks poll).
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY || !store) return;
        try {
          const parsed = e.newValue ? (JSON.parse(e.newValue) as string[]) : [];
          store.setState({ ids: new Set(parsed) });
        } catch {
          /* malformed value — ignore */
        }
      });
    }
  }
  return store;
}

export function useDetachedBgChats<T>(selector: (s: DetachedBgChatsStore) => T): T {
  return useStore(getStore(), selector);
}

/** Imperative clear (no React) — used by account-scope on user switch (FRONT-732). */
export function clearDetachedBgChats(): void {
  getStore().getState().clear();
}
